import { createContext, useContext, useMemo, useState } from "react";
import GridTable from "../../../library/custom/gridTable/GridTable";
import NORow from "../../../library/custom/noRow/noRow";
import { TableLoader } from "../../../library/common/Loader";
import { toPlainText } from "../utils/plainText";
import {
  GRID_DEFAULT_PAGE_SIZE,
  GRID_PAGE_SIZE_OPTIONS,
  PAGE_SIZE,
} from "../../../constant/constant";

// A dashboard table only shows a rows-per-page dropdown when the caller supplies
// onPageSizeChange (see rowsPerPageOptions below), so that is what picks the default:
// GRID_DEFAULT_PAGE_SIZE for a real dropdown, PAGE_SIZE for the fixed single-option
// footer every other section uses.

export const RefetchingContext = createContext(false);

// Dashboard free text arrives as stored editor markup ("<p>…&nbsp;…</p>"). Every grid in
// the feature funnels through this component, so flatten cell values to plain text in one
// place instead of in ~30 column definitions. We *compose* with any existing
// valueFormatter/renderCell rather than replacing it — toPlainText is the identity for
// numbers, dates and markup-free strings, so date/percent/enum formatters keep working
// while `location`-style fallbacks and the `topTypes` join get cleaned too.
// Display only: getRowId reads the raw row, so row identity and the drill-down filter
// values sent back to the API remain the exact stored strings.
export function normalizeColumns(columns) {
  return (columns || []).map((col) => {
    const next = { ...col };
    if (col.valueFormatter) {
      next.valueFormatter = (params) =>
        col.valueFormatter({ ...params, value: toPlainText(params.value) });
    } else if (!col.type || col.type === "string") {
      // Free-text columns never declare `type`, so this still covers every markup-carrying
      // column. Typed columns (number/date/boolean) with no formatter of their own keep
      // MUI's type-level default (e.g. gridNumericColDef's toLocaleString) instead of losing
      // it to an unconditional plain-text formatter that would strip a *number's* commas.
      next.valueFormatter = (params) => toPlainText(params.value);
    }
    if (col.renderCell) {
      next.renderCell = (params) =>
        col.renderCell({
          ...params,
          value: toPlainText(params.value),
          formattedValue: toPlainText(params.formattedValue),
        });
    }
    return next;
  });
}

// GridTable hardcodes paginationMode="server" and never slices `rows` itself, so the
// caller must hand it only the current page. It also unconditionally wraps onPageSizeChange,
// so omitting a handler throws the moment DataGrid invokes it.
// serverMode: rows are already just the current page (fetched via usePaginatedList) — pagination
// is driven by the caller's page/onPageChange/rowCount. Client mode (default) slices `rows` here.
//
// onPageSizeChange (optional): supplying it opts the table into a real rows-per-page
// control instead of the fixed single-size footer. [10, 25, 50, 100] is the standard
// option set across the dashboard's server-paged lists; the current `pageSize` is always
// folded in (and the set deduped) so a caller passing an odd value — e.g. DrillModal's
// pageSize:10, already in the list, or a future caller seeded with something outside the
// standard set — never hands MUI's DataGrid a `value` that isn't among its own `options`,
// which is what triggers its "out-of-range value" console warning. When the prop is
// omitted, behaviour is unchanged from before: a single-option list matching the current
// page size (so the rows-per-page dropdown has nothing to pick but today's size) and a
// noop handler, since GridTable calls onPageSizeChange unconditionally either way.
const DashboardTable = ({
  columns,
  rows,
  getRowId,
  testId,
  // Declared before pageSize so pageSize's default can read it.
  onPageSizeChange,
  pageSize = onPageSizeChange ? GRID_DEFAULT_PAGE_SIZE : PAGE_SIZE,
  onRowClick,
  serverMode = false,
  page: serverPage,
  onPageChange: serverOnPageChange,
  rowCount: serverRowCount,
  loading,
  // Opt-in: suppresses the app-wide full-row hover (reportDashboard.scss's
  // `.rdDataGrid--noRowHover`) for grids whose rows carry no onRowClick —
  // today that's only CredentialsSection's Lessons/Courses tables, where
  // the only click target is the Assigned/Completed/Not Completed cell.
  // Every other dashboard grid keeps the default row hover as its click
  // affordance, so this must stay opt-in rather than the default.
  noRowHover = false,
  // Every dashboard grid shows at most one page of rows — a handful — so none of
  // them needs MUI's virtualized windowing, and all of them want to be as tall as
  // their content. Without this they inherit `.table_style_ui`'s fixed 430px
  // (App.scss), so a 4-row breakdown renders 162px of table above ~270px of dead
  // space plus a scrollbar; v26's own `.tbl-scroll` only ever grows to content.
  // Overridable per call site in case a future caller wants the fixed box back.
  autoHeight = true,
}) => {
  const [localPage, setLocalPage] = useState(0);
  // Client mode's local slice size also has to track a caller-driven pageSize change —
  // otherwise picking "50 per page" would resize the footer's page count but keep
  // slicing 10 rows at a time. Server mode ignores this entirely; the caller's own
  // fetchPage/usePaginatedList already requests the right page size.
  const [localPageSize, setLocalPageSize] = useState(pageSize);

  const pagedRows = useMemo(() => {
    const safe = rows || [];
    if (serverMode) return safe;
    const start = localPage * localPageSize;
    return safe.slice(start, start + localPageSize);
  }, [rows, localPage, localPageSize, serverMode]);
  const safeColumns = useMemo(() => normalizeColumns(columns), [columns]);
  const safeRows = rows || [];

  const page = serverMode ? serverPage : localPage;
  const effectivePageSize = serverMode ? pageSize : localPageSize;
  const rowCount = serverMode ? serverRowCount : safeRows.length;
  const onPageChange = serverMode ? serverOnPageChange : setLocalPage;

  const rowsPerPageOptions = onPageSizeChange
    ? Array.from(new Set([...GRID_PAGE_SIZE_OPTIONS, effectivePageSize])).sort(
        (a, b) => a - b,
      )
    : [effectivePageSize];

  const handlePageSizeChange = (nextPageSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(nextPageSize);
    } else if (!serverMode) {
      // Rows-per-page control is hidden (rowsPerPageOptions has one entry) whenever no
      // handler was supplied, so this branch is effectively unreachable in practice —
      // kept only so a future direct call can't silently resize the footer without also
      // resetting to page 0, mirroring usePaginatedList's own setPageSize contract.
      setLocalPageSize(nextPageSize);
      setLocalPage(0);
    }
  };

  // MUI's autoHeight sizes the grid purely from rows × rowHeight — with 0 rows
  // that's 0px, so the root collapses to just the header and the NoRowsOverlay
  // (NORow's illustration) has to render inside a near-0px box clipped by
  // .MuiDataGrid-main's own overflow:hidden. Every non-dashboard table in the
  // app already renders this correctly because it never uses autoHeight — it
  // just takes the fixed height the app-wide `.table_style_ui` rule gives it.
  // Falling back to that same fixed-height, non-auto behaviour for the empty
  // case (via `--empty` below, reportDashboard.scss) sidesteps the internal
  // layout math entirely instead of trying to out-compute it.
  const isEmpty = rowCount === 0;

  const pageRefetching = useContext(RefetchingContext);
  const showLoading = loading && !pageRefetching;

  return (
    <div>
      {/* rdDataGrid carries the dashboard's grid styling (reportDashboard.scss) and must live
          here, on the component itself, rather than on a page-level ancestor class: MUI's
          Dialog portals its content (DrillModal / RecordModal) to document.body, outside
          .rdPage, so a .rdPage-scoped selector would never reach the grid rendered there. */}
      <div
        className={`table_style_ui rdDataGrid${isEmpty ? " table_style_ui--empty" : ""}${noRowHover ? " rdDataGrid--noRowHover" : ""}${onRowClick ? " rdDataGrid--clickableRows" : ""}`}
      >
        <GridTable
          columns={safeColumns}
          rows={pagedRows}
          getRowId={getRowId || ((r) => r.id)}
          rowCount={rowCount}
          page={page}
          pageSize={effectivePageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={onPageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={onRowClick}
          hideFooterPagination={rowCount <= effectivePageSize}
          autoHeight={autoHeight && !isEmpty}
          // Match reportDashboard.scss's `.table_style_ui.rdDataGrid` !important
          // row/header heights (32/34px) so autoHeight's own layout math sizes
          // the container to the rows actually painted instead of the 52/56px
          // defaults, which left dead space under every dashboard table.
          rowHeight={32}
          headerHeight={34}
          disableSelectionOnClick
          loading={showLoading}
          testId={testId}
          // LoadingOverlay + NoRowsOverlay is the pairing every other grid in
          // the app uses (see admin/component/**/{assetTable,clientList}.jsx):
          // the shared TableLoader is a translucent absolute overlay with its
          // own dark-mode handling, so the grid keeps its size and rows while
          // fetching instead of being swapped out.
          components={{ LoadingOverlay: TableLoader, NoRowsOverlay: NORow }}
        />
      </div>
    </div>
  );
};

export default DashboardTable;
